import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { CartService } from "src/app/services/cart.service";
import { CheckoutService } from "src/app/services/checkout.service";
import { resolveImageUrl } from "src/app/core/utils/image.util";
import Swal from "sweetalert2";

@Component({
  selector: "app-detail-product",
  templateUrl: "./detail-product.component.html",
  styleUrls: ["./detail-product.component.css"],
})
export class DetailProductComponent implements OnInit {
  quantity: number = 1;
  selectedSize: string = "";
  selectedColor: string = "";
  selectedSizeStock: number | null = null;
  availableColors: string[] = [];
  showSizeGuide = false;
  sizeTable: string[][] = [];
  availableStock: number = 0;

  // Galería de Imágenes
  productImages: string[] = [];
  selectedImage: string = '';

  // Tab Activo en Detalles
  activeTab: 'desc' | 'specs' | 'shipping' | 'reviews' = 'desc';

  // Ubicación de Envío
  selectedCity: string = 'Bogotá';

  colorMap: { [key: string]: string } = {
    rojo: "#ff4d4d",
    azul: "#4d79ff",
    verde: "#4dff88",
    negro: "#111827",
    blanco: "#ffffff",
    amarillo: "#ffe066",
    gris: "#9ca3af",
    naranja: "#ffb84d",
    rosa: "#ff4da6",
    morado: "#a64dff",
    marrón: "#a0522d",
    multicolor: "multicolor"
  };

  constructor(
    private cartService: CartService,
    private checkoutService: CheckoutService,
    private router: Router,
    public dialogRef: MatDialogRef<DetailProductComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.loadSizeTable();
  }

  ngOnInit(): void {
    if (this.data && this.data.product) {
      // Configurar galería de imágenes
      const p = this.data.product;
      this.extractProductImages(p);

      // Configurar tallas y colores
      if (p.sizes && p.sizes.length > 0) {
        this.selectSize(p.sizes[0]);
      } else {
        this.availableStock = p.stock ?? p.quantity ?? 99;
      }
    }
  }

  extractProductImages(product: any): void {
    const images: string[] = [];
    if (product.imageUrl) {
      if (Array.isArray(product.imageUrl)) {
        images.push(...product.imageUrl);
      } else if (typeof product.imageUrl === 'string') {
        const trimmed = product.imageUrl.trim();
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
          try {
            const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
            if (Array.isArray(parsed)) images.push(...parsed);
            else images.push(trimmed);
          } catch {
            images.push(trimmed);
          }
        } else if (trimmed.includes(',')) {
          images.push(...trimmed.split(',').map(s => s.trim()));
        } else {
          images.push(trimmed);
        }
      }
    }
    if (product.image && !images.includes(product.image)) {
      images.push(product.image);
    }
    if (images.length === 0) {
      images.push('assets/img/no-image.png');
    }
    this.productImages = images;
    this.selectedImage = images[0];
  }

  fetchRealShippingQuote(): void {
    // Cotización de envío sólo aplica durante la fase de Checkout
  }

  onCityChange(): void {}

  resolveImg(imgUrl: any): string {
    if (!imgUrl) return 'assets/img/no-image.png';
    return resolveImageUrl(imgUrl);
  }

  onImageError(event: any): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect width="200" height="200" fill="%23f1f5f9"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="14">Sin Imagen</text></svg>';
    }
  }

  selectSize(size: any): void {
    this.selectedSize = size.valor;
    this.availableColors = size.colors ? size.colors.map((c: any) => c.color) : [];
    if (this.availableColors.length > 0) {
      this.selectedColor = this.availableColors[0];
    }
    this.availableStock = size.colors ? size.colors.reduce((acc: number, curr: any) => acc + curr.cant, 0) : (size.cant || 0);
  }

  getAvailableStock(): number {
    if (this.data?.product?.sizes && this.data.product.sizes.length > 0) {
      if (!this.selectedSize || !this.selectedColor) return 0;
      const size = this.data.product.sizes.find((s: any) => s.valor === this.selectedSize);
      if (!size) return 0;
      const color = size.colors?.find((c: any) => c.color === this.selectedColor);
      return color ? color.cant : (size.cant || 0);
    }
    return this.data?.product?.stock ?? this.data?.product?.quantity ?? 99;
  }

  getColorStyle(color: string) {
    const lower = (color || '').toLowerCase().trim();
    if (lower === 'multicolor') {
      return {
        background: 'linear-gradient(135deg, #ff4d4d 0%, #ffe066 33%, #4dff88 66%, #4d79ff 100%)'
      };
    }
    return {
      background: this.colorMap[lower] || '#e2e8f0'
    };
  }

  loadSizeTable(): void {
    const category = this.data?.product?.category;
    if (category === "calzado") {
      this.sizeTable = [
        ["EU 36", "22.5 cm"],
        ["EU 37", "23.5 cm"],
        ["EU 38", "24.0 cm"],
        ["EU 39", "25.0 cm"],
        ["EU 40", "25.5 cm"],
        ["EU 41", "26.5 cm"],
        ["EU 42", "27.0 cm"],
      ];
    } else if (category === "ropa") {
      this.sizeTable = [
        ["S", "Busto: 84-88", "Cintura: 64-68", "Cadera: 88-92"],
        ["M", "Busto: 88-92", "Cintura: 68-72", "Cadera: 92-96"],
        ["L", "Busto: 92-96", "Cintura: 72-76", "Cadera: 96-100"],
        ["XL", "Busto: 96-100", "Cintura: 76-80", "Cadera: 100-104"],
      ];
    } else {
      this.sizeTable = [["Medidas Estándar Internacionales - Ajuste Regular"]];
    }
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  addToCart(): void {
    if (this.data.product.sizes && this.data.product.sizes.length > 0 && (!this.selectedSize || !this.selectedColor)) {
      Swal.fire({
        icon: "warning",
        title: "Selecciona Opciones",
        text: "Por favor selecciona una talla y un color disponibles.",
        confirmButtonColor: "#4f46e5",
      });
      return;
    }

    const availableStock = this.getAvailableStock();
    if (availableStock > 0 && this.quantity > availableStock) {
      Swal.fire({
        icon: "error",
        title: "Stock Insuficiente",
        text: `Solo quedan ${availableStock} unidades disponibles en la combinación seleccionada.`,
        confirmButtonColor: "#ef4444",
      });
      return;
    }

    this.cartService.addToCart(
      this.data.product,
      this.quantity,
      this.selectedSize,
      this.selectedColor
    );

    Swal.fire({
      icon: "success",
      title: "¡Producto Añadido!",
      text: `${this.data.product.name} fue agregado a tu carrito.`,
      timer: 2000,
      showConfirmButton: false,
    });

    this.dialogRef.close();
  }

  buyNow(): void {
    if (this.data.product.sizes && this.data.product.sizes.length > 0 && (!this.selectedSize || !this.selectedColor)) {
      Swal.fire({
        icon: "warning",
        title: "Selecciona Opciones",
        text: "Por favor selecciona una talla y un color disponibles.",
        confirmButtonColor: "#4f46e5",
      });
      return;
    }

    const availableStock = this.getAvailableStock();
    if (availableStock > 0 && this.quantity > availableStock) {
      Swal.fire({
        icon: "error",
        title: "Stock Insuficiente",
        text: `Solo quedan ${availableStock} unidades disponibles en la combinación seleccionada.`,
        confirmButtonColor: "#ef4444",
      });
      return;
    }

    this.cartService.addToCart(
      this.data.product,
      this.quantity,
      this.selectedSize,
      this.selectedColor
    );

    this.dialogRef.close();
    this.router.navigate(['/cart']);
  }

  get total(): number {
    return this.quantity * (this.data?.product?.price || 0);
  }

  increaseQuantity(): void {
    const max = this.getAvailableStock() || 99;
    if (this.quantity < max) {
      this.quantity++;
    }
  }

  deincreaseQuantity(): void {
    if (this.quantity > 1) {
      this.quantity--;
    }
  }

  validateQuantity(): void {
    const max = this.getAvailableStock() || 99;
    if (this.quantity > max) {
      this.quantity = max;
    } else if (this.quantity < 1) {
      this.quantity = 1;
    }
  }
}
